import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/Home.css";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTruck, faHeadset, faUndo, faLock, faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";

// Import category JSONs
import menProducts from "../products/men-products.json";
import womenProducts from "../products/women-products.json";
import kidsProducts from "../products/kids-products.json";
import electronicsProducts from "../products/electronics-products.json";
import beautyProducts from "../products/beauty-products.json";
import jewelleryProducts from "../products/jewellery-products.json";

function Home() {
  const navigate = useNavigate();
  const [dealsNew, setDealsNew] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [featured, setFeatured] = useState([]);

  const handleShopNow = () => navigate("/products");
  const handleViewAllProducts = () => navigate("/products");

  const shuffleArray = (arr) => [...arr].sort(() => Math.random() - 0.5);

  // Combine and tag all products with their category
  const allProducts = useMemo(() => {
    const tag = (arr, cat) => (arr || []).map((p) => ({ ...p, category: cat }));
    return [
      ...tag(menProducts.men, "men"),
      ...tag(womenProducts.women, "women"),
      ...tag(kidsProducts.kids, "kids"),
      ...tag(electronicsProducts.electronics, "electronics"),
      ...tag(beautyProducts.beauty, "beauty"),
      ...tag(jewelleryProducts.jewellery, "jewellery"),
    ];
  }, []);

  // Deals & New Arrivals
  useEffect(() => {
    if (!allProducts || allProducts.length === 0) {
      setDealsNew([]);
      return;
    }

    const shuffled = shuffleArray(allProducts);

    const deals = shuffled.slice(0, 3).map((p) => {
      const price = Number(p.price) || 0;
      return {
        ...p,
        tag: "Deal",
        discountedPrice: Math.round(price * 0.8),
      };
    });

    const arrivals = shuffled.slice(3, 6).map((p) => ({
      ...p,
      tag: "New",
    }));

    const items = [...deals, ...arrivals];
    const withClones = [items[items.length - 1], ...items, items[0]];

    setDealsNew(withClones);
    setActiveIndex(1);
  }, [allProducts]);

  // Featured Products — correctly tagged with categories
  useEffect(() => {
    const getTop3 = (arr, cat) =>
      shuffleArray(
        (arr || [])
          .sort((a, b) => b.rating - a.rating)
          .slice(0, 3)
          .map((p) => ({ ...p, category: cat }))
      );

    const combined = [
      ...getTop3(menProducts.men, "men"),
      ...getTop3(womenProducts.women, "women"),
      ...getTop3(kidsProducts.kids, "kids"),
      ...getTop3(electronicsProducts.electronics, "electronics"),
      ...getTop3(beautyProducts.beauty, "beauty"),
      ...getTop3(jewelleryProducts.jewellery, "jewellery"),
    ];

    setFeatured(shuffleArray(combined));
  }, []);

  return (
    <div className="home">
      {/* Hero Banner */}
      <section
        className="hero-banner"
        style={{ backgroundImage: "url('/banner.png')" }}
      >
        <div className="hero-content">
          <h1>Welcome to ShopSphere</h1>
          <p>Your One-Stop Shop for All Your Needs</p>
          <button className="cta-button" onClick={handleShopNow}>
            Shop Now
          </button>
        </div>
      </section>

      {/* Deals & New Arrivals */}
      <section className="deals-arrivals-section">
        <h2>Deals & New Arrivals</h2>
        {dealsNew.length > 0 && (
          <div className="carousel-container">
            <div
              className="banner-wrapper"
              style={{
                transform: `translateX(-${activeIndex * 100}%)`,
                transition: "transform 0.7s ease-in-out",
              }}
            >
              {dealsNew.map((item, idx) => (
                <div
                  key={idx}
                  className="banner-slide"
                  style={{
                    backgroundImage: `url('/deals-arrivals-banner.png')`,
                  }}
                >
                  <div className="banner-overlay">
                    <div className="banner-left">
                      <span
                        className={`banner-badge ${
                          item.tag === "Deal" ? "deal" : "new"
                        }`}
                      >
                        {item.tag === "Deal" ? "🔥 Hot Deal" : "✨ New Arrival"}
                      </span>
                      <h3 className="banner-name">{item.name}</h3>
                      {item.tag === "Deal" ? (
                        <p className="banner-price">
                          <span className="discount">
                            ₹{item.discountedPrice}
                          </span>
                          <span className="original">₹{item.price}</span>
                        </p>
                      ) : (
                        <p className="banner-price single">₹{item.price}</p>
                      )}
                      <p className="banner-excerpt">
                        {item.description
                          ? item.description.slice(0, 120) + "..."
                          : ""}
                      </p>
                      <button
                        className="cta-button small"
                        onClick={() =>
                          navigate("/products", {
                            state: {
                              openProduct: item,
                              category: item.category || "all",
                            },
                          })
                        }
                      >
                        View
                      </button>
                    </div>
                    <div className="banner-right">
                      <img src={item.images[0]} alt={item.name} />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Arrows */}
            {activeIndex > 0 && (
              <button
                className="carousel-arrow left"
                onClick={() => setActiveIndex(activeIndex - 1)}
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
            )}
            {activeIndex < dealsNew.length - 1 && (
              <button
                className="carousel-arrow right"
                onClick={() => setActiveIndex(activeIndex + 1)}
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            )}
          </div>
        )}
      </section>

      {/* Categories Section */}
      <section className="categories-section">
        <h2>Shop by Category</h2>
        <div className="categories-container">
          {[
            {
              name: "Men",
              img: "/category/men.png",
              link: "/products?category=men",
            },
            {
              name: "Women",
              img: "/category/women.png",
              link: "/products?category=women",
            },
            {
              name: "Kids",
              img: "/category/kids.png",
              link: "/products?category=kids",
            },
            {
              name: "Electronics",
              img: "/category/electronics.png",
              link: "/products?category=electronics",
            },
            {
              name: "Beauty",
              img: "/category/beauty.png",
              link: "/products?category=beauty",
            },
            {
              name: "Jewellery",
              img: "/category/jewellery.png",
              link: "/products?category=jewellery",
            },
          ].map((cat) => (
            <div
              key={cat.name}
              className="category-card"
              onClick={() => navigate(cat.link)}
            >
              <div className="category-image">
                <img src={cat.img} alt={cat.name} />
              </div>
              <div className="category-name">{cat.name}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Featured Products */}
      <section className="featured-products-section">
        <h2>Featured Products</h2>
        <div className="featured-container">
          {featured.map((prod, idx) => (
            <div key={idx} className="featured-card">
              <div className="featured-image">
                <img src={prod.images[0]} alt={prod.name} />
              </div>
              <h3>{prod.name}</h3>
              <p className="price">Price: ₹{prod.price}</p>
              <p className="rating">
                Rating: ⭐ {prod.rating} ({prod.reviews} reviews)
              </p>
              <button
                className="cta-button small"
                onClick={() =>
                  navigate("/products", {
                    state: {
                      openProduct: prod,
                      category: prod.category || "all",
                    },
                  })
                }
              >
                View
              </button>
            </div>
          ))}
        </div>
        <button className="cta-button" onClick={handleViewAllProducts}>
          View All Products
        </button>
      </section>

      {/* Testimonials */}
      <section className="testimonials-section">
        <h2>Our Key Features</h2>
        <div className="testimonials-container">
          <div className="testimonial-card">
            <FontAwesomeIcon className="nav-icon" icon={faTruck} />
            <h3>Free Shipping</h3>
            <p>Enjoy free shipping on all orders over ₹1000 across India.</p>
          </div>
          <div className="testimonial-card">
            <FontAwesomeIcon className="nav-icon" icon={faHeadset} />
            <h3>24/7 Support</h3>
            <p>Our support team is here to help you anytime, anywhere.</p>
          </div>
          <div className="testimonial-card">
            <FontAwesomeIcon className="nav-icon" icon={faUndo} />
            <h3>Easy Returns</h3>
            <p>
              Not satisfied? Return your product within 30 days for a full
              refund.
            </p>
          </div>
          <div className="testimonial-card">
            <FontAwesomeIcon className="nav-icon" icon={faLock} />
            <h3>Secure Payment</h3>
            <p>Your payment information is processed securely with SSL.</p>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Home;